import React, { useEffect, useState, useRef } from "react";
import { Camera, Image, Video, Star, Zap, ArrowUpRight, Sparkles } from "lucide-react";

const Services = () => {
  const [visible, setVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("services");
      if (section) {
        const top = section.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        if (top < windowHeight * 0.7) {
          setVisible(true);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMouseMove = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const services = [
    {
      icon: Camera,
      title: "جلسات تصوير",
      subtitle: "احترافية",
      desc: "تصوير بورتريه، عائلي، وزفاف بجودة عالية",
      gradient: "from-[#667eea] via-[#764ba2] to-[#f093fb]",
      glowColor: "rgba(102, 126, 234, 0.4)",
    },
    {
      icon: Image,
      title: "تعديل وريتاتش",
      subtitle: "احترافي",
      desc: "تحسين الألوان وإخراج صور نهائية مذهلة",
      gradient: "from-[#f093fb] via-[#f5576c] to-[#ffd200]",
      glowColor: "rgba(245, 87, 108, 0.4)",
    },
    {
      icon: Video,
      title: "فيديوهات",
      subtitle: "4K",
      desc: "تصوير وتحرير فيديوهات دعائية واحتفالية",
      gradient: "from-[#4facfe] via-[#00f2fe] to-[#43e97b]",
      glowColor: "rgba(79, 172, 254, 0.4)",
    },
    {
      icon: Star,
      title: "منتجات وإعلانات",
      subtitle: "تجارية",
      desc: "إظهار منتجاتك بأفضل صورة ممكنة",
      gradient: "from-[#fa709a] via-[#fee140] to-[#30cfd0]",
      glowColor: "rgba(250, 112, 154, 0.4)",
    },
    {
      icon: Zap,
      title: "أزياء وفاشون",
      subtitle: "عصري",
      desc: "جلسات تصوير موضة بأسلوب احترافي",
      gradient: "from-[#a8edea] via-[#fed6e3] to-[#d299c2]",
      glowColor: "rgba(168, 237, 234, 0.4)",
    },
  ];

  return (
    <section 
      className="relative bg-black py-32 overflow-hidden" 
      id="services"
      ref={containerRef}
      onMouseMove={handleMouseMove}
    >
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }} />
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-purple-500 rounded-full blur-3xl opacity-20 animate-pulse" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Mouse Follow Light */}
      <div 
        className="absolute w-96 h-96 rounded-full pointer-events-none transition-all duration-300 ease-out"
        style={{
          left: mousePosition.x - 192,
          top: mousePosition.y - 192,
          background: 'radial-gradient(circle, rgba(139, 92, 246, .2) 0%, transparent 70%)',
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div
            className={`inline-flex items-center gap-3 bg-gradient-to-r from-purple-500/20 to-blue-500/20 backdrop-blur-xl border border-white/10 px-6 py-3 rounded-full mb-8 transition-all duration-1000 ${
              visible ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
          >
            <Sparkles className="w-5 h-5 text-purple-400 animate-pulse" />
            <span className="text-white/90 font-medium tracking-wider">SERVICES</span>
          </div>
          
          <h2
            className={`text-6xl md:text-7xl font-black mb-6 transition-all duration-1000 delay-100 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <span className="bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent">
              خدماتنا
            </span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              الاحترافية
            </span>
          </h2>
          
          <p
            className={`text-gray-400 text-xl max-w-2xl mx-auto transition-all duration-1000 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            نحول رؤيتك إلى واقع بصري مذهل
          </p>
        </div>

        {/* Services Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {services.map((service, index) => {
            const Icon = service.icon;
            const isLarge = index === 0 || index === 3;
            
            return (
              <div
                key={index}
                className={`group relative transition-all duration-700 ${
                  isLarge ? 'md:col-span-1 lg:col-span-1' : ''
                } ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden group-hover:border-white/20 transition-all duration-500">
                  {/* Gradient Overlay */}
                  <div 
                    className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-700`}
                  />
                  
                  {/* Glow Effect */}
                  <div 
                    className="absolute -inset-[1px] rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl"
                    style={{ 
                      background: `linear-gradient(135deg, ${service.glowColor}, transparent)`,
                    }}
                  />

                  {/* Content */}
                  <div className="relative h-full p-8 flex flex-col">
                    {/* Icon with Glow */}
                    <div className="mb-6 relative">
                      <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} blur-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-700`} />
                      <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-700 shadow-2xl`}>
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>

                    {/* Title */}
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-white mb-1 group-hover:translate-x-1 transition-transform duration-500">
                        {service.title}
                      </h3>
                      <p className={`text-lg font-medium bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`}>
                        {service.subtitle}
                      </p>
                    </div>

                    {/* Description */}
                    <p className="text-gray-400 leading-relaxed mb-6 flex-grow group-hover:text-gray-300 transition-colors duration-500">
                      {service.desc}
                    </p>

                    {/* Action Button */}
                    <div className="flex items-center gap-2 text-white font-medium opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                      <span className="text-sm">استكشف المزيد</span>
                      <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                    </div>

                    {/* Corner Shine */}
                    <div className="absolute top-0 right-0 w-32 h-32 opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
                      <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} blur-3xl`} />
                    </div>

                    {/* Animated Border */}
                    <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                      <div className={`absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r ${service.gradient} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700`} />
                      <div className={`absolute inset-y-0 right-0 w-[2px] bg-gradient-to-b ${service.gradient} transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-700 delay-200`} />
                      <div className={`absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-l ${service.gradient} transform origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-700 delay-400`} />
                      <div className={`absolute inset-y-0 left-0 w-[2px] bg-gradient-to-t ${service.gradient} transform origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-700 delay-600`} />
                    </div>
                  </div>

                  {/* Number Badge */}
                  <div className={`absolute top-6 left-6 w-10 h-10 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white text-sm font-black shadow-2xl transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-500`}>
                    {index + 1}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-20 transition-all duration-1000 delay-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <button className="group relative px-10 py-5 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 text-white rounded-full font-bold text-lg shadow-2xl hover:shadow-purple-500/50 transition-all duration-500 hover:scale-105 overflow-hidden">
            <span className="relative z-10 flex items-center gap-3">
              ابدأ مشروعك الآن
              <ArrowUpRight className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-pink-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            {/* Shine Animation */}
            <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000">
              <div className="h-full w-10 bg-white/30 skew-x-12" />
            </div>
          </button>
        </div>
      </div>

      {/* Floating Particles */}
      {visible && (
        <>
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/20 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${10 + Math.random() * 10}s`,
              }}
            />
          ))}
        </>
      )}

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) translateX(50px);
            opacity: 0;
          }
        }
        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </section>
  );
};

export default Services;